/**
 * CatalogService — SuperAdmin Katalog Ürünleri istemcisi.
 *
 * Katalog TÜM şirketlerin ortak kullandığı global referans listesidir ve HER ZAMAN ana
 * GTS veritabanından okunur (backend: ICatalogRepository → MainDbContext). Bu yüzden
 * isteklerde X-Company-Id gönderilmez; erişim kontrolü sunucudaki [SuperAdminOnly]
 * guard'ına dayanır.
 *
 * İstek deseni SuperAdminService.netApiRequest ile aynıdır: own-JWT Bearer başlığı ve
 * REACT_APP_NETAPI_URL tabanı. Tek fark yolun /api/superadmin/catalog olması.
 */

import ownAuthSession from './auth/ownAuthSession';
import type {
  CatalogBulkCreateResponse,
  CatalogBulkResult,
  CatalogCategory,
  CatalogPagedResult,
  CatalogProduct,
  CatalogProductPayload,
  CatalogProductQuery,
  CatalogSector,
  CatalogSummary,
  CatalogSummaryQuery,
} from '../components/superadmin/catalog/types/catalog.types';

const NETAPI_URL = process.env.REACT_APP_NETAPI_URL || 'https://gtsnetapi.aquasoft.com.tr';

async function getAuthToken(): Promise<string> {
  const token = await ownAuthSession.getAccessToken();
  if (!token) {
    throw new Error('Kullanıcı oturum açmamış');
  }
  return token;
}

async function catalogRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  try {
    const token = await getAuthToken();

    const response = await fetch(`${NETAPI_URL}/api/superadmin/catalog${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error((data as any)?.message || 'Katalog isteği başarısız oldu');
    }

    return data as T;
  } catch (error) {
    console.error('Katalog API hatası:', error);
    throw error;
  }
}

/**
 * Tanımsız/boş değerleri atarak query string üretir. `isActive: false` gibi anlamlı
 * "falsy" değerler korunur; yalnızca undefined, null ve boş metin elenir.
 */
/** Tüm yazma uçları POST — CRITICAL_API_DEVELOPMENT_RULES gereği PUT/DELETE yok. */
function catalogPost<T>(endpoint: string, body: unknown): Promise<T> {
  return catalogRequest<T>(endpoint, { method: 'POST', body: JSON.stringify(body) });
}

function buildQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.append(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

// ════════════════════════════════════════════════════════════════
// Ürünler — okuma
// ════════════════════════════════════════════════════════════════

/**
 * Sunucu taraflı sayfalı ürün listesi. İstemci tarafında dilimleme YAPILMAZ; 23.186
 * kayıtlık katalogda bu tarayıcıyı kilitler.
 */
export async function getProductsPaged(query: CatalogProductQuery): Promise<CatalogPagedResult> {
  const response = await catalogRequest<{
    success: boolean;
    data: CatalogProduct[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }>(`/products/paged${buildQuery({ ...query })}`);

  return {
    items: response.data ?? [],
    totalCount: response.totalCount ?? 0,
    page: response.page ?? query.page,
    pageSize: response.pageSize ?? query.pageSize,
    totalPages: response.totalPages ?? 0,
  };
}

/** İstatistik kartları — liste ile aynı filtreleri alır, filtreyle senkron kalır. */
export async function getSummary(query: CatalogSummaryQuery = {}): Promise<CatalogSummary> {
  const response = await catalogRequest<{ success: boolean; data: CatalogSummary }>(
    `/products/summary${buildQuery({ ...query })}`
  );

  // Backend null alanları atlıyor; sayaçlar hiç ürün yokken eksik gelebilir.
  return {
    totalProducts: response.data?.totalProducts ?? 0,
    activeProducts: response.data?.activeProducts ?? 0,
    sectorCount: response.data?.sectorCount ?? 0,
    categoryCount: response.data?.categoryCount ?? 0,
    unclassifiedProducts: response.data?.unclassifiedProducts ?? 0,
  };
}

export async function getProductById(id: number): Promise<CatalogProduct> {
  const response = await catalogRequest<{ success: boolean; data: CatalogProduct }>(
    `/products/${id}`
  );
  return response.data;
}

// ════════════════════════════════════════════════════════════════
// Sektörler ve kategoriler
// ════════════════════════════════════════════════════════════════

export async function getSectors(includeCounts = false): Promise<CatalogSector[]> {
  const response = await catalogRequest<{ success: boolean; data: CatalogSector[] }>(
    `/sectors${buildQuery({ includeCounts: includeCounts || undefined })}`
  );
  return response.data ?? [];
}

/**
 * Kategoriler. `sectorId` verilirse yalnızca o sektörünkiler döner — filtrenin
 * kademeli daralması buna dayanır.
 */
export async function getCategories(
  sectorId?: number,
  includeCounts = false
): Promise<CatalogCategory[]> {
  const response = await catalogRequest<{ success: boolean; data: CatalogCategory[] }>(
    `/categories${buildQuery({ sectorId, includeCounts: includeCounts || undefined })}`
  );
  return response.data ?? [];
}

/**
 * Barkodlar query string'de virgülle gönderiliyor; binlerce barkod tek istekte URL
 * sınırını aşar. Bu yüzden istekler parçalanır.
 */
const BARCODE_CHECK_CHUNK = 200;

/**
 * Verilen barkodlardan katalogda zaten kayıtlı olanları döner (barkod → ürün Id).
 * Barkod ana DB'de UNIQUE olduğu için kaydetmeden önce çakışmayı yakalamakta kullanılır.
 */
export async function checkBarcodes(barcodes: string[]): Promise<Record<string, number>> {
  const list = Array.from(new Set(barcodes.map((b) => b.trim()).filter(Boolean)));
  if (list.length === 0) return {};

  const merged: Record<string, number> = {};
  for (let i = 0; i < list.length; i += BARCODE_CHECK_CHUNK) {
    const chunk = list.slice(i, i + BARCODE_CHECK_CHUNK);
    const response = await catalogRequest<{ success: boolean; data: Record<string, number> }>(
      `/products/barcode-check${buildQuery({ barcodes: chunk.join(',') })}`
    );
    Object.assign(merged, response.data ?? {});
  }
  return merged;
}

// ════════════════════════════════════════════════════════════════
// Ürünler — yazma
// ════════════════════════════════════════════════════════════════

export async function createProduct(payload: CatalogProductPayload): Promise<CatalogProduct> {
  const response = await catalogPost<{ success: boolean; message: string; data: CatalogProduct }>(
    '/products/create',
    payload
  );
  return response.data;
}

export async function updateProduct(payload: CatalogProductPayload): Promise<CatalogProduct> {
  const response = await catalogPost<{ success: boolean; message: string; data: CatalogProduct }>(
    '/products/update',
    payload
  );
  return response.data;
}

export async function deleteProduct(id: number): Promise<string> {
  const response = await catalogPost<{ success: boolean; message: string }>('/products/delete', {
    id,
  });
  return response.message;
}

// ════════════════════════════════════════════════════════════════
// Toplu işlemler
// ════════════════════════════════════════════════════════════════

/**
 * Excel içe aktarma. Backend tek çağrıda en fazla 2000 satır kabul ediyor; çağıran
 * taraf 500'lük batch'lerle gönderir. Barkodu katalogda zaten olan veya listede
 * mükerrer geçen satırları backend kendisi eleyip `failedItems` içinde raporlar.
 */
export function bulkCreateProducts(
  items: CatalogProductPayload[]
): Promise<CatalogBulkCreateResponse> {
  return catalogPost<CatalogBulkCreateResponse>('/products/bulk-create', { items });
}

export function bulkDeleteProducts(ids: number[]): Promise<CatalogBulkResult> {
  return catalogPost<CatalogBulkResult>('/products/bulk-delete', { ids });
}

export function bulkUpdateStatus(ids: number[], isActive: boolean): Promise<CatalogBulkResult> {
  return catalogPost<CatalogBulkResult>('/products/bulk-update-status', { ids, isActive });
}

/** Sektör/kategori taşıma. `categoryId` boş bırakılırsa ürünler kategorisiz kalır. */
export function bulkMoveProducts(
  ids: number[],
  sectorId: number,
  categoryId?: number | null
): Promise<CatalogBulkResult> {
  return catalogPost<CatalogBulkResult>('/products/bulk-move', {
    ids,
    sectorId,
    categoryId: categoryId ?? null,
  });
}

export function bulkUpdateVat(
  ids: number[],
  vatRate: number | null,
  vatStatus: string | null
): Promise<CatalogBulkResult> {
  return catalogPost<CatalogBulkResult>('/products/bulk-update-vat', { ids, vatRate, vatStatus });
}

// ════════════════════════════════════════════════════════════════
// Taksonomi — yazma
// ════════════════════════════════════════════════════════════════

/** Backend get-or-create çalışır: aynı adla ikinci kayıt açılmaz, mevcut döner. */
export async function createSector(name: string): Promise<CatalogSector> {
  const response = await catalogPost<{ success: boolean; data: CatalogSector }>('/sectors/create', {
    name,
  });
  return response.data;
}

export function updateSector(payload: {
  id: number;
  name: string;
  sortOrder?: number;
  isActive?: boolean;
}): Promise<{ success: boolean; message: string }> {
  return catalogPost('/sectors/update', payload);
}

export function deleteSector(id: number): Promise<{ success: boolean; message: string }> {
  return catalogPost('/sectors/delete', { id });
}

export async function createCategory(payload: {
  sectorId: number;
  parentId?: number | null;
  name: string;
}): Promise<CatalogCategory> {
  const response = await catalogPost<{ success: boolean; data: CatalogCategory }>(
    '/categories/create',
    payload
  );
  return response.data;
}

export function updateCategory(payload: {
  id: number;
  name: string;
  sortOrder?: number;
  isActive?: boolean;
}): Promise<{ success: boolean; message: string }> {
  return catalogPost('/categories/update', payload);
}

export function deleteCategory(id: number): Promise<{ success: boolean; message: string }> {
  return catalogPost('/categories/delete', { id });
}

const CatalogService = {
  getProductsPaged,
  getSummary,
  getProductById,
  getSectors,
  getCategories,
  checkBarcodes,
  bulkCreateProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  bulkDeleteProducts,
  bulkUpdateStatus,
  bulkMoveProducts,
  bulkUpdateVat,
  createSector,
  updateSector,
  deleteSector,
  createCategory,
  updateCategory,
  deleteCategory,
};

export default CatalogService;
